<template>
    <div>
        <form @submit.prevent>
            <div>
                <base-input v-model="selectedTranslation.title" label="Title" placeholder="New translation" />
            </div>

            <div>
                <label class="base-input__label">Languages</label>
                <el-select v-model="selectedTranslation.languages" multiple>
                    <el-option
                        v-for="language in possibleLanguages"
                        :key="language.value"
                        :value="language.value"
                        :label="language.label"
                    />
                </el-select>
            </div>

            <div class="mb-sm">
                <label class="base-input__label">Files to be translated</label>
                <FileUpload
                    multiple
                    @addFile="onFileChangeToBeTranslated"
                    @deleteFile="onFileRemoveToBeTranslated"
                    :fileList="toBeTranslatedFilesToUpload"
                    :fileType="'.pdf,.xls,.xlsx,.doc,.docx,.txt,.csv'"
                />

                <PopupInput title="Add support materials?">
                    <div class="pop-input-content">
                        <FileUpload
                            multiple
                            @addFile="onFileChangeSupport"
                            @deleteFile="onFileRemoveSupport"
                            :fileList="supportFilesToUpload"
                            :fileType="'.pdf,.xls,.xlsx,.doc,.docx,.txt,.csv'"
                        />
                    </div>
                </PopupInput>
            </div>

            <PopupInput title="Add notes?">
                <div class="pop-input-content">
                    <base-textarea label="Insert your notes" v-model="selectedTranslation.notes.byClient" :setStyle="{minHeight: '200px'}"></base-textarea>
                </div>
            </PopupInput>

            <div v-if="selectedTranslation.files.support.length > 0" class="mb-sm">
                <label class="base-input__label">Support materials</label>
                <div class="file-preview form-control closed" disabled v-for="file in selectedTranslation.files.support" :key="file.url">
                    <div>
                        <i class="fa-solid fa-file-pdf icon"></i>
                    </div>
                    <div>
                        <p class="font-weight-bold">{{ file.title }}</p>
                        <p class="text-muted" v-if="file.size">{{ file.size }}</p>
                    </div>
                </div>
            </div>

            <div>
                <label class="base-input__label">Deadline</label>
                <el-date-picker
                    class="el-date-padding-order"
                    placeholder="No set deadline"
                    v-model="deadlineIntermediate"
                    type="date"
                />
            </div>

            <div class="mt-sm d-flex flex-column">
                <base-button @click="submitTranslationRequest">
                    Request translation
                </base-button>
            </div>
        </form>
    </div>
</template>

<script>
import { Table, TableColumn, DatePicker, Select, Option } from "element-ui";
import { BaseInput, BaseTextarea, BaseButton, FileUpload } from "@/components"
import PopupInput from "@/pages/Tools/BlogPostParagraphs/PopupInput.vue";

import Translation from '../Entities/Translation';

import { formatDate } from "../Helpers/formatters";
import { apiHandler } from "@/util/errorHandling";


export default {
    components: {
        [Table.name]: Table,
        [TableColumn.name]: TableColumn,
        [Select.name]: Select,
        [Option.name]: Option,
        BaseInput,
        BaseTextarea,
        BaseButton,
        [DatePicker.name]: DatePicker,
        PopupInput,
        FileUpload,
    },
    props: {
        disabled: {
            type: Boolean,
            required: false,
            default: false
        },
        /** @type {{label: string, value: string}[]} */
        possibleLanguages: {
            type: Array,
            required: false,
            default: () => [],
        }
    },
    data() {
        return {
            /** @type Translation */
            selectedTranslation: null,
            /** @type File[]|null */
            toBeTranslatedFilesToUpload: null,
            /** @type File[]|null */
            supportFilesToUpload: null,
            // The datepicker component uses a different format than our object,
            // this is an intermediate variable used to handle conversions
            deadlineIntermediate: null,
        }
    },
    beforeMount() {
        this.selectedTranslation = Translation.defaultNew()
    },
    methods: {
        async submitTranslationRequest() {
            const payload = new FormData()
            payload.append('title', this.selectedTranslation.title)
            payload.append('notes', this.selectedTranslation.notes.byClient ?? '')
            this.selectedTranslation.languages?.map(lang => payload.append('languages[]', lang))
            this.toBeTranslatedFilesToUpload?.forEach(file => payload.append('files_to_be_translated[]', file))
            this.supportFilesToUpload?.forEach(file => payload.append('support_files[]', file))

            if (this.selectedTranslation.timestamps.deadline) {
                payload.append('deadline', this.selectedTranslation.timestamps.deadline)
            }

            await apiHandler(async () => {
                let response = await this.$axios.post('tools/translation-manager/client/create-translation', payload)
                const newTranslation = new Translation(response.data)
                this.selectedTranslation = newTranslation
                this.$emit('submit', this.newTranslation)
            })
        },

        onFileChangeToBeTranslated(files) {
			this.toBeTranslatedFilesToUpload = files
            this.$forceUpdate()
		},
        onFileRemoveToBeTranslated(file) {
			this.toBeTranslatedFilesToUpload = this.toBeTranslatedFilesToUpload.filter(
				(item) => item.name !== file.name
			);

            this.$forceUpdate()
		},

        onFileChangeSupport(files) {
			this.supportFilesToUpload = files
            this.$forceUpdate()
		},
        onFileRemoveSupport(file) {
			this.supportFilesToUpload = this.supportFilesToUpload.filter(
				(item) => item.name !== file.name
			);

            this.$forceUpdate()
		},
    },
    watch: {
        deadlineIntermediate: {
            handler(newValue) {
                this.selectedTranslation.timestamps.deadline = formatDate(newValue)
            }
        }
    }
}
</script>
